import React, { useRef } from 'react';
import { CheckCircle, TrendingUp, ArrowUpRight, Percent } from 'lucide-react';
import { motion } from 'framer-motion';
import CodingBackground from './CodingBackground';

const GrowthSection = () => {
  const chartData = [30, 50, 75, 95];

  return (
    <section className="relative overflow-hidden py-24 bg-background transition-colors duration-300">
      <CodingBackground />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary/5 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" style={{ perspective: "1000px" }}>

        {/* LEFT CONTENT */}
        <motion.div
          initial={{ opacity: 0, y: 80, rotateX: 20, z: -100 }}
          whileInView={{ opacity: 1, y: 0, rotateX: 0, z: 0 }}
          transition={{ type: "spring", stiffness: 50, damping: 22, mass: 1 }}
          viewport={{ once: false, margin: "-100px", amount: 0.2 }}
          style={{ transformStyle: "preserve-3d" }}
        >
          <p className="text-sm font-semibold tracking-widest text-primary uppercase font-secondary">
            Our Impact
          </p>
          <h2 className="text-4xl md:text-5xl font-extrabold text-foreground leading-tight mt-4 font-heading">
            Driving Business Growth <br />
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              With Data-Driven Solutions
            </span>
          </h2>
          <p className="mt-6 text-foreground/70 leading-relaxed font-body text-lg">
            We combine high-performance architecture, automation, and intelligent analytics to create scalable systems that maximize efficiency and ROI.
          </p>

          {/* FEATURES */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              'Performance Optimization',
              'Automation Systems',
              'Advanced Analytics',
              'Conversion Optimization',
            ].map((item, idx) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, x: -50, rotateY: 30 }}
                whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
                transition={{ delay: 0.1 * idx, type: "spring" }}
                viewport={{ once: false }}
                className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border shadow-sm hover:shadow-md transition-shadow"
              >
                <CheckCircle className="w-5 h-5 text-primary" />
                <span className="text-card-foreground font-medium text-sm">{item}</span>
              </motion.div>
            ))}
          </div>

          {/* PROGRESS BAR */}
          <div className="mt-12 p-6 rounded-2xl bg-card border border-border shadow-sm">
            <div className="flex justify-between text-sm font-semibold text-card-foreground mb-3">
              <span>System Reliability</span>
              <span className="text-primary">99.9%</span>
            </div>
            <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: '99.9%' }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                viewport={{ once: false }}
                className="h-full bg-gradient-to-r from-primary via-accent to-primary rounded-full relative"
              >
                <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent)] -translate-x-full animate-[shimmer_2s_infinite]"></div>
              </motion.div>
            </div>
          </div>

          {/* <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            className="mt-10 group relative flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground font-semibold rounded-full overflow-hidden shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-shadow duration-300"
          >
            <span className="relative z-10">Discover More</span>
            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out"></div>
          </motion.button> */}
        </motion.div>

        {/* RIGHT SIDE - CHART */}
        <div className="relative flex items-center justify-center py-10 lg:py-0">
          {/* Ambient Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 bg-primary/20 blur-[100px] rounded-full pointer-events-none"></div>

          <motion.div
            initial={{ opacity: 0, scale: 0.85, rotateY: -15, rotateX: 10, z: -100 }}
            whileInView={{ opacity: 1, scale: 1, rotateY: 0, rotateX: 0, z: 0 }}
            transition={{ type: 'spring', stiffness: 45, damping: 20, mass: 1 }}
            viewport={{ once: false }}
            style={{ transformStyle: "preserve-3d" }}
            className="relative w-full max-w-md bg-card/80 backdrop-blur-xl border border-border rounded-3xl p-6 shadow-2xl"
          >
            {/* Chart Header */}
            <div className="flex items-center justify-between mb-8 pb-4 border-b border-border">
              <div>
                <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                  Performance Metrics
                </h4>
                <h3 className="text-xl font-bold text-card-foreground mt-1 flex items-center gap-2">
                  <Percent className="w-5 h-5 text-primary" />
                  Growth Overview
                </h3>
              </div>
              <div className="flex items-center gap-1.5 bg-primary/10 px-4 py-2 rounded-full border border-primary/20">
                <TrendingUp className="w-4 h-4 text-primary" />
                <span className="text-sm font-bold text-primary">+216%</span>
              </div>
            </div>

            {/* Chart Area */}
            <div className="relative h-64 mt-4">
              {/* Grid Lines */}
              <div className="absolute inset-0 flex flex-col justify-between">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="border-t border-border w-full transition-colors"
                  />
                ))}
              </div>

              {/* Bars Container */}
              <div className="absolute inset-0 flex items-end justify-around px-2">
                {chartData.map((height, index) => {
                  const barHeight = (height / 100) * 100; // Relative to container
                  const percentage = [30, 50, 75, 95][index];
                  return (
                    <div key={index} className="relative flex flex-col items-center h-full justify-end">
                      <motion.div
                        initial={{ height: 0 }}
                        whileInView={{ height: `${barHeight}%` }}
                        transition={{
                          duration: 1.2,
                          delay: index * 0.15,
                          ease: [0.34, 1.26, 0.64, 1]
                        }}
                        viewport={{ once: false }}
                        className="w-10 sm:w-12 bg-gradient-to-t from-primary/80 to-accent rounded-t-xl relative group"
                      >
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.9 + index * 0.15 }}
                          className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-foreground text-background text-xs font-bold px-2 py-1 rounded shadow-lg pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          {percentage}%
                        </motion.div>
                        {/* Shimmer effect inside bar */}
                        <div className="absolute inset-0 overflow-hidden rounded-t-xl">
                          <div className="w-full h-full bg-[linear-gradient(0deg,transparent,rgba(255,255,255,0.2),transparent)] -translate-y-full hover:animate-[shimmer-vertical_1.5s_infinite]"></div>
                        </div>
                      </motion.div>
                      <motion.span
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ delay: 1.1 + index * 0.1 }}
                        className="text-[11px] font-semibold text-muted-foreground mt-3 uppercase tracking-widest"
                      >
                        {index === 0 ? 'Q1' : index === 1 ? 'Q2' : index === 2 ? 'Q3' : 'Q4'}
                      </motion.span>
                    </div>
                  );
                })}
              </div>

              {/* Curvy Line with Arrow Tip */}
              <div className="absolute inset-0 pointer-events-none pb-8 h-full">
                <svg width="100%" height="100%" viewBox="0 0 400 200" preserveAspectRatio="none">
                  <defs>
                    <marker
                      id="arrowhead-growth"
                      markerWidth="10"
                      markerHeight="10"
                      refX="9"
                      refY="5"
                      orient="auto"
                    >
                      <path
                        d="M0 0 L10 5 L0 10 L2 5 Z"
                        fill="currentColor"
                        className="text-primary"
                      />
                    </marker>
                    <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.2" />
                      <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="1" />
                    </linearGradient>
                  </defs>
                  <motion.path
                    initial={{ pathLength: 0, opacity: 0 }}
                    whileInView={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 2, delay: 0.8, ease: "easeInOut" }}
                    viewport={{ once: false }}
                    d="M 20 180 Q 140 100 200 120 T 380 20"
                    fill="transparent"
                    stroke="url(#lineGrad)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    markerEnd="url(#arrowhead-growth)"
                    className="drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]"
                  />
                </svg>
              </div>
            </div>

            {/* Growth Message */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3 }}
              className="mt-8 bg-primary/5 p-4 rounded-xl border border-primary/10 backdrop-blur-sm"
            >
              <p className="text-sm text-card-foreground flex items-center justify-center gap-2 flex-wrap">
                <span className="text-primary font-bold">📈 Systems Scaled</span>
                <span className="text-muted-foreground">from</span>
                <span className="font-bold text-foreground">30%</span>
                <span className="text-muted-foreground">to</span>
                <span className="font-bold text-primary">95%+</span>
                <span className="text-muted-foreground">efficiency</span>
              </p>
            </motion.div>

            {/* Stats Cards */}
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { label: 'Baseline', value: '30%', delay: 1.4 },
                { label: 'Current', value: '95%', delay: 1.5, highlight: true },
                { label: 'Growth', value: '+216%', delay: 1.6, highlight: true },
              ].map((stat) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: stat.delay }}
                  className={`text-center py-3 rounded-xl border ${stat.highlight ? 'bg-primary/5 border-primary/20' : 'bg-background/50 border-border'}`}
                >
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">{stat.label}</p>
                  <p className={`text-lg font-bold ${stat.highlight ? 'text-primary' : 'text-foreground'}`}>
                    {stat.value}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Floating Arrow Top Right */}
            <motion.div
              animate={{ y: [0, -10, 0], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-6 -right-6 text-primary/30 pointer-events-none"
            >
              <ArrowUpRight size={64} strokeWidth={1} />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
        @keyframes shimmer-vertical {
          100% { transform: translateY(100%); }
        }
      `}</style>
    </section>
  );
};

export default GrowthSection;